import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import './Login.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const navigate = useNavigate();
  const { login } = useAuth();

  // Token validation state
  const [tokenStatus, setTokenStatus] = useState('loading'); // 'loading' | 'valid' | 'invalid' | 'no-token'
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenReason, setTokenReason] = useState('');

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // On mount: validate token (or show payment-required screen)
  useEffect(() => {
    if (!token) {
      setTokenStatus('no-token');
      return;
    }

    api.get('/auth/validate-token', { params: { token } })
      .then(res => {
        if (res.data.valid) {
          setTokenEmail(res.data.email);
          setTokenStatus('valid');
        } else {
          setTokenReason(res.data.reason || 'This link has expired or already been used.');
          setTokenStatus('invalid');
        }
      })
      .catch(() => {
        setTokenReason('Could not validate your link. Please try again.');
        setTokenStatus('invalid');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || null;
      let data;
      try {
        const res = await api.post('/auth/complete-signup', { token, name, password });
        data = res.data;
      } catch (apiErr) {
        setError(apiErr.response?.data?.error || 'Could not activate account');
        setLoading(false);
        return;
      }

      // Refresh auth context then go to dashboard
      if (login) await login(tokenEmail, password).catch(() => {});
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // ── Loading state while validating token ──────────────────────────────────
  if (tokenStatus === 'loading') {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
          <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 16px', color: 'var(--primary-color)' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Validating your link…</p>
        </div>
      </div>
    );
  }

  // ── No token: payment required ────────────────────────────────────────────
  if (tokenStatus === 'no-token') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Outreach Engine</h1>
            <p>Payment required to sign up</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: 1.6 }}>
              To create an account, please choose a plan first. After payment you'll receive an email with a link to set your password.
            </p>
            <Link
              to="/pricing"
              className="login-button"
              style={{ display: 'inline-block', textDecoration: 'none' }}
            >
              View Pricing
            </Link>
          </div>
          <div className="auth-footer">
            <span>Already have an account?</span>
            <Link to="/login" className="auth-link">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Invalid / expired token ───────────────────────────────────────────────
  if (tokenStatus === 'invalid') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Outreach Engine</h1>
            <p>Link expired or already used</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: 1.6 }}>
              {tokenReason} If you need help, please contact support or repurchase a plan.
            </p>
            <Link
              to="/pricing"
              className="login-button"
              style={{ display: 'inline-block', textDecoration: 'none' }}
            >
              View Pricing
            </Link>
          </div>
          <div className="auth-footer">
            <span>Already have an account?</span>
            <Link to="/login" className="auth-link">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Valid token: account completion form ──────────────────────────────────
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Outreach Engine</h1>
          <p>Activate your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={tokenEmail}
              readOnly
              disabled
              style={{ backgroundColor: 'var(--surface-strong)', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="firstName">First Name <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
              placeholder="Your first name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              placeholder="Your last name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="At least 8 characters"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirm">Confirm Password</label>
            <div className="password-wrapper">
              <input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Repeat your password"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirm(v => !v)}
                tabIndex={-1}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Activating account…' : 'Activate Account'}
          </button>
        </form>

        <div className="auth-footer">
          <span>Already have an account?</span>
          <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
